/* eslint-disable import/no-cycle */
import { login, register as registerApi, logout as logoutApi, user as userApi } from '../api/auth';
import { useSubscriptionPlanStore } from '../store/subscription.store';
import { useFeatureUsageStore } from '../store/feature-usage.store';
import { useOnboardingChecklist } from '../store/onboarding-checklist.store';
import { usePartnerStore } from '../store/partner.store';
import { useApplyConfirmationStore } from '../store/apply-confirmation.store';
/* eslint-enable import/no-cycle */
import { clearSentryUser, syncSentryUser } from '../errors/sentry-user';
import { BEACH_COMUTE_USERS } from '../storage';
import { hasServerSession } from '../utils/helpers';

/**
 * True when the server rendered a completed onboarding onto the SPA mount element
 * (welcome.blade.php: <div id="app" data-steps-completed>). Mirrors hasServerSession()'s
 * data-user-id read in utils/helpers.js.
 */
const hasCompletedOnboardingOnServer = () => {
	return Boolean(document.getElementById('app')?.dataset.stepsCompleted);
};

/**
 * The logout in progress or already done for the current user. Module-scoped because every
 * setAuthState() call builds its own closures, and the router guard and the onboarding form
 * each hold one. A second click on Log Out while the page is leaving for /login would
 * otherwise send another POST /logout that the browser aborts (EN-586).
 */
let pendingLogout = null;

export const setAuthState = () => {
	const isAuthenticated = () => {
		return getUser() !== null;
	};

	const userHaveAccessTo = (feature) => {
		return !!getUser()?.features?.[feature];
	};
    
	const redirectIntoOnboardingV3 = () => {
		window.location.href = '/onboarding/v3/upload';
	};

	// The server is authoritative: the Inertia onboarding flow writes steps_completed
	// server-side without touching localStorage, so a stale cached user can claim onboarding
	// is unfinished long after it is done. Falls back to the cached user where no
	// server-rendered shell exists (guests, preview.blade.php).
	const isOnboardingComplete = () => {
		return hasCompletedOnboardingOnServer() || !!getUser()?.steps_completed;
	};
    
	const isKajabiUser = () => {
		return getUser()?.options?.is_kajabi_user ?? false;
	};

	const isEmailInDomain = (domain) => {
		return getUser()?.email?.endsWith(domain) ?? false;
	};
    
	const setUser = (user) => {
		// A newly signed-in user has a session of their own to end.
		pendingLogout = null;
		localStorage.setItem('user', JSON.stringify(user));
	};

	const getUser = () => {
		const raw = localStorage.getItem('user');
		if (raw && raw !== 'undefined') {
			return JSON.parse(raw);
		}
		return null;
	};

	// The Inertia onboarding handoff authenticates via the Laravel session but never
	// writes the SPA's localStorage user. When the server rendered an authenticated id
	// (hasServerSession) and there is no cached user yet, pull the user from /api/user so
	// the SPA boots logged-in — otherwise the router guard reads empty localStorage and
	// bounces the freshly-authenticated user back to the login page.
	const hydrateServerSession = async () => {
		if (isAuthenticated() || ! hasServerSession()) {
			return;
		}

		// `client` (api/client.js) already unwraps the axios response to its body, which is
		// the JsonResource envelope `{ data: <user> }`; destructuring `data` yields the user.
		// Hydrate via setUserData (matching the legacy login path) so the subscription/
		// feature/onboarding/partner stores are populated too, not just localStorage.
		const { data } = await userApi();
		setUserData(data);
	};

	const authenticate = async (loginPayload) => {
		if (Object.hasOwn(loginPayload, 'modal_name') && loginPayload?.modal_name == null) {
			delete loginPayload.modal_name;
		}

		const { data } = await login(loginPayload);
		setUserData(data);
		return data;
	};

	const register = async (registerPayload) => {
		if (Object.hasOwn(registerPayload, 'modal_name') && registerPayload?.modal_name == null) {
			delete registerPayload.modal_name;
		}

		const { data } = await registerApi(registerPayload);
		setUserData(data);
		return data;
	};

	/**
	 * Forget the signed-in user. The SPA has never had a single place that drops auth state —
	 * logout, the two 401 transports and account deletion each did their own localStorage surgery
	 * — so this is it. Being the one place also makes it the one place Sentry's identity is
	 * cleared: on a shared device the next person's errors must not be attributed to the account
	 * that just left (EN-960).
	 */
	const clearUser = () => {
		localStorage.removeItem('user');
		localStorage.removeItem('currentPage');
		clearSentryUser();
		useApplyConfirmationStore().clear();
	};

	const logout = () => {
		if (pendingLogout) {
			return pendingLogout;
		}

		pendingLogout = (async () => {
			try {
				await logoutApi();
			} catch (error) {
				// Let a failed logout be retried instead of replaying the failure.
				pendingLogout = null;
				throw error;
			} finally {
				clearUser();
			}
		})();

		return pendingLogout;
	};
    
	const setUserData = (data) => {
		// Persist the SPA's auth source-of-truth (localStorage) FIRST, before instantiating any
		// Pinia store. On the Inertia login handoff setUserData runs during boot; if a store hook
		// throws here, the router guard would otherwise see an empty localStorage and bounce the
		// freshly-authenticated user to /login — aborting the in-flight /api/user request.
		setUser(data);

		// EN-960: identify the user for Sentry from the same funnel that owns the SPA's
		// auth source-of-truth. ID and non-PII tags only - see errors/sentry-user.js.
		syncSentryUser(data);

		const { setUserPlan } = useSubscriptionPlanStore();
		const featureUsageStore = useFeatureUsageStore();
		const onboardingChecklistStore = useOnboardingChecklist();
		const partnerStore = usePartnerStore();

		setUserPlan(data?.current_plan);
		featureUsageStore.setFeatureUsage(data?.feature_usage);
		onboardingChecklistStore.setOnboardingChecklist(data);

		if (data?.organization) {
			partnerStore.setPartner(data.organization);
		} else {
			partnerStore.clearPartner();
		}
	};

	const isBeachComuteUser = () => {
		const encode = btoa(getUser()?.email);
		return BEACH_COMUTE_USERS.includes(encode);
	};

	const isWonsultingUser = () => isEmailInDomain('@wonsulting.com');

	return {
		getUser,
		setUser,
		clearUser,
		isAuthenticated,
		hydrateServerSession,
		authenticate,
		register,
		logout,
		userHaveAccessTo,
		setUserData,
		isEmailInDomain,
		isKajabiUser,
		isBeachComuteUser,
		isOnboardingComplete,
		isWonsultingUser,
		redirectIntoOnboardingV3,
	};
};
